import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ROLE_LABELS, type Role } from '@agencia/shared';
import { useCurrentUser } from '@/auth/AuthContext';
import { Alert, Button, Field } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';

interface UserItem {
  id: string;
  nome: string;
  email: string;
  role: Role;
  ativo: boolean;
  mfaAtivo: boolean;
  deveTrocarSenha: boolean;
  bloqueado: boolean;
  ultimoLoginEm: string | null;
  contaAzulVendedorId: string | null;
  digisacUserId: string | null;
}

interface VendedorContaAzul {
  id: string;
  nome: string;
  usuarioId: string | null;
}

/** Resultado que mostra a senha temporária UMA vez para o admin repassar ao usuário. */
interface TempPassword {
  nome: string;
  email: string;
  senha: string;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return 'Nunca entrou';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Bahia' }).format(new Date(iso));
}

function statusOf(u: UserItem): { label: string; tone: 'ok' | 'warn' | 'off' } {
  if (!u.ativo) return { label: 'Desativado', tone: 'off' };
  if (u.bloqueado) return { label: 'Bloqueado', tone: 'warn' };
  if (u.deveTrocarSenha || !u.mfaAtivo) return { label: 'Aguardando 1º acesso', tone: 'warn' };
  return { label: 'Ativo', tone: 'ok' };
}

export function UsersPage() {
  const me = useCurrentUser();
  const [users, setUsers] = useState<UserItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [temp, setTemp] = useState<TempPassword | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  /** Nulo enquanto o Conta Azul não estiver conectado: a coluna fica oculta. */
  const [vendedores, setVendedores] = useState<VendedorContaAzul[] | null>(null);
  /** Nulo enquanto a Digisac não estiver configurada: a coluna fica oculta. */
  const [atendentes, setAtendentes] = useState<VendedorContaAzul[] | null>(null);

  const load = useCallback(async () => {
    try {
      setUsers(await api.get<UserItem[]>('/users'));
    } catch (err) {
      setError(errorMessage(err));
    }
    try {
      setVendedores(await api.get<VendedorContaAzul[]>('/integrations/conta-azul/vendedores'));
    } catch {
      setVendedores(null);
    }
    try {
      setAtendentes(await api.get<VendedorContaAzul[]>('/integrations/digisac/users'));
    } catch {
      setAtendentes(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function action(u: UserItem, fn: () => Promise<unknown>, confirmText?: string) {
    if (confirmText && !window.confirm(confirmText)) return;
    setError(null);
    setNotice(null);
    setBusyId(u.id);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Usuários</h1>
          <p className="muted">Cadastre vendedores e gestores. Cada pessoa cria a própria senha e o 2FA no primeiro acesso.</p>
        </div>
        <Button onClick={() => setCreating(true)}>Novo usuário</Button>
      </header>

      {error && <Alert>{error}</Alert>}
      {notice && <Alert kind="success">{notice}</Alert>}

      {creating && (
        <CreateUserForm
          onCancel={() => setCreating(false)}
          onCreated={(t) => {
            setCreating(false);
            setTemp(t);
            void load();
          }}
        />
      )}

      {temp && <TempPasswordCard temp={temp} onClose={() => setTemp(null)} />}

      <section className="card flush">
        {!users ? (
          <p className="muted pad">Carregando…</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Nome</th>
                  <th scope="col">Perfil</th>
                  {vendedores && <th scope="col">Vendedor no Conta Azul</th>}
                  {atendentes && <th scope="col">Atendente na Digisac</th>}
                  <th scope="col">Situação</th>
                  <th scope="col">Último acesso</th>
                  <th scope="col">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const st = statusOf(u);
                  const isMe = u.id === me.id;
                  const busy = busyId === u.id;
                  return (
                    <tr key={u.id} className={u.ativo ? undefined : 'row--off'}>
                      <td>
                        <strong>{u.nome}</strong>
                        {isMe && <span className="tag">você</span>}
                        <br />
                        <span className="muted small">{u.email}</span>
                      </td>
                      <td>
                        {isMe ? (
                          'Administrador'
                        ) : (
                          <select
                            aria-label={`Perfil de ${u.nome}`}
                            value={u.role}
                            disabled={busy}
                            onChange={(e) =>
                              void action(u, () => api.patch(`/users/${u.id}`, { role: e.target.value }))
                            }
                          >
                            {(['VENDEDOR', 'GESTOR', 'ADMIN'] as Role[]).map((r) => (
                              <option key={r} value={r}>
                                {ROLE_LABELS[r]}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      {vendedores && (
                        <td>
                          <select
                            aria-label={`Vendedor do Conta Azul de ${u.nome}`}
                            value={u.contaAzulVendedorId ?? ''}
                            disabled={busy}
                            onChange={(e) =>
                              void action(u, async () => {
                                await api.patch(`/users/${u.id}`, { contaAzulVendedorId: e.target.value || null });
                                setNotice(`Vínculo de ${u.nome} atualizado. As vendas dos meses abertos foram reatribuídas.`);
                              })
                            }
                          >
                            <option value="">Não ligado</option>
                            {vendedores
                              .filter((v) => !v.usuarioId || v.usuarioId === u.id)
                              .map((v) => (
                                <option key={v.id} value={v.id}>
                                  {v.nome}
                                </option>
                              ))}
                          </select>
                        </td>
                      )}
                      {atendentes && (
                        <td>
                          <select
                            aria-label={`Atendente da Digisac de ${u.nome}`}
                            value={u.digisacUserId ?? ''}
                            disabled={busy}
                            onChange={(e) =>
                              void action(u, async () => {
                                await api.patch(`/users/${u.id}`, { digisacUserId: e.target.value || null });
                                setNotice(`Atendente da Digisac de ${u.nome} atualizado.`);
                              })
                            }
                          >
                            <option value="">Não ligado</option>
                            {atendentes
                              .filter((a) => !a.usuarioId || a.usuarioId === u.id)
                              .map((a) => (
                                <option key={a.id} value={a.id}>
                                  {a.nome}
                                </option>
                              ))}
                          </select>
                        </td>
                      )}
                      <td>
                        <span className={`badge badge--${st.tone}`}>{st.label}</span>
                      </td>
                      <td className="small">{formatDateTime(u.ultimoLoginEm)}</td>
                      <td className="actions">
                        {u.bloqueado && (
                          <Button variant="secondary" disabled={busy} onClick={() => void action(u, () => api.post(`/users/${u.id}/unlock`))}>
                            Desbloquear
                          </Button>
                        )}
                        {!isMe && (
                          <>
                            <Button
                              variant="secondary"
                              disabled={busy}
                              onClick={() =>
                                void action(
                                  u,
                                  async () => {
                                    const r = await api.post<{ senhaTemporaria: string }>(`/users/${u.id}/reset-password`);
                                    setTemp({ nome: u.nome, email: u.email, senha: r.senhaTemporaria });
                                  },
                                  `Gerar uma nova senha temporária para ${u.nome}? A senha atual deixará de funcionar.`,
                                )
                              }
                            >
                              Nova senha
                            </Button>
                            {u.mfaAtivo && (
                              <Button
                                variant="secondary"
                                disabled={busy}
                                onClick={() =>
                                  void action(
                                    u,
                                    async () => {
                                      await api.post(`/users/${u.id}/reset-mfa`);
                                      setNotice(`2FA de ${u.nome} redefinido. No próximo acesso, ele cadastra o celular novamente.`);
                                    },
                                    `Redefinir o 2FA de ${u.nome}? Use quando a pessoa trocar ou perder o celular.`,
                                  )
                                }
                              >
                                Redefinir 2FA
                              </Button>
                            )}
                            <Button
                              variant={u.ativo ? 'danger' : 'secondary'}
                              disabled={busy}
                              onClick={() =>
                                void action(
                                  u,
                                  () => api.patch(`/users/${u.id}`, { ativo: !u.ativo }),
                                  u.ativo ? `Desativar ${u.nome}? A pessoa perde o acesso imediatamente.` : undefined,
                                )
                              }
                            >
                              {u.ativo ? 'Desativar' : 'Reativar'}
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function CreateUserForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: (t: TempPassword) => void }) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('VENDEDOR');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const r = await api.post<{ senhaTemporaria: string }>('/users', { nome, email, role });
      onCreated({ nome, email, senha: r.senhaTemporaria });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="card" aria-labelledby="novo-usuario">
      <h2 id="novo-usuario">Novo usuário</h2>
      <form className="grid-form" onSubmit={(e) => void submit(e)}>
        {error && <Alert>{error}</Alert>}
        <Field label="Nome completo" value={nome} onChange={(e) => setNome(e.target.value)} autoFocus required />
        <Field label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <div className="field">
          <label htmlFor="novo-perfil">Perfil</label>
          <select id="novo-perfil" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            <option value="VENDEDOR">Vendedor: vê só as próprias vendas</option>
            <option value="GESTOR">Gestor: define metas e acompanha a equipe</option>
            <option value="ADMIN">Administrador: tudo, inclusive usuários</option>
          </select>
        </div>
        <div className="row">
          <Button type="submit" loading={loading}>
            Criar usuário
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </section>
  );
}

function TempPasswordCard({ temp, onClose }: { temp: TempPassword; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const message = `Seu acesso ao sistema de vendas da Fly Now:\nEndereço: ${window.location.origin}\nE-mail: ${temp.email}\nSenha temporária: ${temp.senha}\n\nNo primeiro acesso você vai criar sua própria senha e ativar a verificação em duas etapas (Google Authenticator ou Microsoft Authenticator).`;

  return (
    <section className="card highlight" aria-labelledby="senha-temp">
      <h2 id="senha-temp">Senha temporária de {temp.nome}</h2>
      <p>
        Repasse para a pessoa por um canal privado (WhatsApp direto ou pessoalmente). Ela{' '}
        <strong>não será mostrada de novo</strong>.
      </p>
      <p className="temp-password">
        <code>{temp.senha}</code>
      </p>
      <div className="row">
        <Button
          variant="secondary"
          onClick={() =>
            void navigator.clipboard.writeText(message).then(
              () => setCopied(true),
              () => setCopied(false),
            )
          }
        >
          {copied ? 'Mensagem copiada!' : 'Copiar mensagem de boas-vindas'}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Já repassei, fechar
        </Button>
      </div>
    </section>
  );
}
